// Epoch 48: Simplified Email Notification Preferences Component
// Date: August 23, 2025
// Purpose: Binary email toggles for notification categories (replaces complex frequency system)

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';

// Simple Switch component to replace HeadlessUI
const Switch: React.FC<{
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}> = ({ checked, onChange, disabled, className }) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      className={className || `${
        checked ? 'bg-blue-600' : 'bg-gray-200'
      } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <span
        className={`${
          checked ? 'translate-x-5' : 'translate-x-0'
        } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
      />
    </button>
  );
};

interface EmailPreferences {
  lead_responses: boolean;
  campaigns: boolean;
  system: boolean;
}

interface NotificationPreferencesProps {
  userId?: string;
  userRole?: 'user' | 'csr' | 'admin';
}

const NotificationPreferencesNew: React.FC<NotificationPreferencesProps> = ({ userId, userRole }) => {
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [preferences, setPreferences] = useState<EmailPreferences>({
    lead_responses: true,
    campaigns: true,
    system: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmailSent, setTestEmailSent] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(false);

  useEffect(() => {
    checkFeatureFlag();
    loadPreferences();
  }, [userId]);

  const checkFeatureFlag = async () => {
    try {
      // Check if new preferences system is enabled
      const { data, error } = await supabase
        .rpc('is_feature_enabled', {
          p_feature_name: 'new_email_preferences',
          p_user_id: userId || (await supabase.auth.getUser()).data.user?.id
        });

      if (!error && data) {
        setFeatureEnabled(true);
      }
    } catch (error) {
      console.log('Feature flag check failed, using old system');
      setFeatureEnabled(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const targetUserId = userId || user?.id;
      
      if (!targetUserId) return;

      const { data, error } = await supabase
        .from('users')
        .select('email_enabled, email_notification_preferences, notification_preferences, role')
        .eq('id', targetUserId)
        .single();

      if (error) {
        console.error('Error loading preferences:', error);
        return;
      }

      // Use new preferences if available, otherwise convert old format
      if (data.email_notification_preferences) {
        setPreferences(data.email_notification_preferences);
        setEmailEnabled(data.email_enabled ?? true);
      } else if (data.notification_preferences) {
        // Convert old format to new
        const oldPrefs = data.notification_preferences;
        setPreferences({
          lead_responses: oldPrefs.lead_response_new !== 'never',
          campaigns: oldPrefs.campaign_approved !== 'never',
          system: true
        });
        setEmailEnabled(true);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (newPrefs: EmailPreferences, newEmailEnabled: boolean) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const targetUserId = userId || user?.id;
      
      if (!targetUserId) return;

      const { error } = await supabase
        .from('users')
        .update({
          email_notification_preferences: newPrefs,
          email_enabled: newEmailEnabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', targetUserId);

      if (error) {
        console.error('Error saving preferences:', error);
        alert('Failed to save preferences. Please try again.');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (category: keyof EmailPreferences) => {
    const newPrefs = { ...preferences, [category]: !preferences[category] };
    setPreferences(newPrefs);
    savePreferences(newPrefs, emailEnabled);
  };

  const handleMasterToggle = () => {
    const newEnabled = !emailEnabled;
    setEmailEnabled(newEnabled);
    savePreferences(preferences, newEnabled);
  };

  const sendTestEmail = async () => {
    try {
      // This would trigger a test email through an Edge Function
      const { error } = await supabase.functions.invoke('send-test-email', {
        body: { 
          type: 'test',
          userId: userId || (await supabase.auth.getUser()).data.user?.id
        }
      });

      if (!error) {
        setTestEmailSent(true);
        setTimeout(() => setTestEmailSent(false), 3000);
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Failed to send test email. Please try again.');
    }
  };

  // Show old component if feature flag is disabled
  if (!featureEnabled) {
    // Return null or import old NotificationPreferences component
    return (
      <div className="text-gray-500 text-sm">
        Notification preferences are being upgraded. Please check back soon.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-4">
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Define categories based on user role
  const getCategories = () => {
    switch (userRole) {
      case 'admin':
        return [
          {
            key: 'system' as keyof EmailPreferences,
            title: 'System Monitoring',
            description: 'System alerts, performance issues, and error notifications'
          },
          {
            key: 'campaigns' as keyof EmailPreferences,
            title: 'User Management',
            description: 'New user signups, trial expirations, and upgrades'
          }
        ];
      case 'csr':
        return [
          {
            key: 'campaigns' as keyof EmailPreferences,
            title: 'Campaign Reviews',
            description: 'New campaign submissions and user responses to feedback'
          },
          {
            key: 'lead_responses' as keyof EmailPreferences,
            title: 'Lead Processing',
            description: 'CSV uploads, filter requests, and reply requests from users'
          },
          {
            key: 'system' as keyof EmailPreferences,
            title: 'System Updates',
            description: 'System announcements and CSR tool updates'
          }
        ];
      default: // user
        return [
          {
            key: 'lead_responses' as keyof EmailPreferences,
            title: 'Lead Response Management',
            description: 'New lead responses, follow-ups, and conversation updates'
          },
          {
            key: 'campaigns' as keyof EmailPreferences,
            title: 'Campaign Management',
            description: 'Campaign approvals, change requests, and status updates'
          },
          {
            key: 'system' as keyof EmailPreferences,
            title: 'System Updates',
            description: 'System announcements and new feature updates'
          }
        ];
    }
  };

  const categories = getCategories();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900">Email Notification Preferences</h3>
        <p className="mt-1 text-sm text-gray-500">
          Choose which notifications you'd like to receive via email. All notifications always appear in your bell icon.
        </p>
      </div>

      {/* Master Toggle */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900">
              📧 Email Notifications
            </h4>
            <p className="text-sm text-gray-500">
              Master toggle to enable or disable all email notifications
            </p>
          </div>
          <Switch
            checked={emailEnabled}
            onChange={handleMasterToggle}
            disabled={saving}
            className={`${
              emailEnabled ? 'bg-blue-600' : 'bg-gray-200'
            } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span
              className={`${
                emailEnabled ? 'translate-x-5' : 'translate-x-0'
              } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
            />
          </Switch>
        </div>
      </div>

      {/* Category Toggles */}
      <div className={`space-y-4 ${!emailEnabled ? 'opacity-50' : ''}`}>
        {categories.map((category) => (
          <div key={category.key} className="flex items-start justify-between py-4 border-b border-gray-200 last:border-0">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">
                {category.title}
              </h4>
              <p className="text-sm text-gray-500 mt-1">
                {category.description}
              </p>
            </div>
            <Switch
              checked={preferences[category.key]}
              onChange={() => handleToggle(category.key)}
              disabled={!emailEnabled || saving}
              className={`${
                preferences[category.key] ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ml-4`}
            >
              <span
                className={`${
                  preferences[category.key] ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
          </div>
        ))}
      </div>

      {/* Mandatory Emails Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Important Notice
            </h3>
            <p className="mt-1 text-sm text-blue-700">
              Security alerts, password resets, and billing notifications cannot be disabled and will always be sent to your email address for account safety.
            </p>
          </div>
        </div>
      </div>

      {/* Test Email Button */}
      <div className="flex justify-end">
        <button
          onClick={sendTestEmail}
          disabled={!emailEnabled || saving}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {testEmailSent ? (
            <>
              <svg className="mr-2 h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Test Email Sent!
            </>
          ) : (
            <>
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Send Test Email
            </>
          )}
        </button>
      </div>

      {/* Saving indicator */}
      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
          <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Saving preferences...
        </div>
      )}
    </div>
  );
};

export default NotificationPreferencesNew;